// ------------------------------- CUSTOM QUICK VIEW --------------------------------

import { formatMoney } from '@theme/money-formatting';

const getMoneyFormat = () => {
  const template =
    document.getElementById('qv-money-format') || document.getElementById('bundle-money-format');
  return template?.content?.textContent?.trim() || '{{amount}}';
};

const getCurrency = () => {
  try {
    return JSON.parse(
      document.getElementById('qv-shop-currency')?.textContent ||
        document.getElementById('bundle-shop-currency')?.textContent ||
        '"INR"'
    );
  } catch {
    return 'INR';
  }
};

const formatVariantPrice = (cents) => formatMoney(Number(cents) || 0, getMoneyFormat(), getCurrency());

const ANIMATION_MS_MOBILE = 350;
const ANIMATION_MS_DESKTOP = 200;

const getQuickViewAnimationMs = () =>
  window.matchMedia('(max-width: 749px)').matches ? ANIMATION_MS_MOBILE : ANIMATION_MS_DESKTOP;

const getQuickViewSettings = () => {
  try {
    const el = document.getElementById('bundle-quick-view-settings');
    return el ? JSON.parse(el.textContent) : {};
  } catch {
    return {};
  }
};

const parseVariantsJson = (raw) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Quick view: failed to parse variant data', error);
    return [];
  }
};

class CustomQuickView extends HTMLElement {
  constructor() {
    super();
    this.currentVariants = [];
    this.selectedVariant = null;
    this.defaultImageSrc = '';
    this.overlayElement = null;

    this.isBundleMode = false;
    this.popupAddButtonText = 'Add To Box';
    this.popupSoldOutText = 'Sold Out';
    this.isClosing = false;
    this.closeTimer = null;

    // Tracks the specific product card wrapper that launched this modal instance
    this.activeCardWrapper = null;
  }

  connectedCallback() {
    this.closeBtn = this.querySelector('.qv-close-btn');
    this.addToBoxWrapper = this.querySelector('.qv-add-to-box-wrapper');
    this.addToBoxBtn = this.querySelector('.qv-submit-btn'); 
    this.addToBoxCrossBtn = this.querySelector('.qv-add-to-box-cross');
    this.addToCartBtn = this.querySelector('.qv-add-to-cart-btn');
    this.swatchContainer = this.querySelector('.qv-swatches');
    this.qvImage = this.querySelector('.qv-image');
    this.qvSelectedValue = this.querySelector('.qv-selected-value');
    this.qvTitle = this.querySelector('.qv-title');
    this.qvPrice = this.querySelector('.qv-price');
    this.qvMrp = this.querySelector('.qv-mrp');
    this.qvDiscount = this.querySelector('.qv-discount');

    this.closeBtn?.addEventListener('click', () => this.hide());
    this.swatchContainer?.addEventListener('click', (e) => {
      const swatch = e.target.closest('.qv-swatch');
      if (!swatch || swatch.disabled) return;
      const index = Number(swatch.dataset.index);
      if (!Number.isNaN(index)) this.selectVariant(index);
    });
    this.initLocalTriggers();
    this.initAddToBoxHandlers();
    this.initAddToCartHandler();
    this.initParentGridCrossHandler();
  }

  applyModeUI() {
    if (this.isBundleMode) {
      this.addToBoxBtn?.classList.remove('hidden');
      this.addToCartBtn?.classList.add('hidden');
    } else {
      this.addToBoxBtn?.classList.add('hidden');
      this.addToCartBtn?.classList.remove('hidden');
    }
  }

  /**
   * Targets click events directly at the action buttons to capture correct parent context.
   */
  initLocalTriggers() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.open-quickview-btn');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const wrapper = btn.closest('.bundle-buy-btn-wrapper');
      const isBundleTrigger =
        btn.getAttribute('data-template') === 'bundle' ||
        Boolean(document.querySelector('.sticky-added-product-container'));

      if (isBundleTrigger && wrapper?.classList.contains('is-added')) {
        return;
      }

      if (isBundleTrigger && (wrapper?.classList.contains('is-bundle-locked') || (typeof window.isBundleFull === 'function' && window.isBundleFull()))) {
        return;
      }

      this.open(btn);
    });
  }

  open(triggerButton) {
    this.activeCardWrapper = triggerButton.closest('.bundle-buy-btn-wrapper');
    this.isBundleMode =
      triggerButton.getAttribute('data-template') === 'bundle' ||
      Boolean(document.querySelector('.sticky-added-product-container'));

    const quickViewSettings = getQuickViewSettings();
    this.popupAddButtonText =
      triggerButton.getAttribute('data-popup-add-text') ||
      quickViewSettings.addButtonText ||
      'Add To Box';
    this.popupSoldOutText =
      triggerButton.getAttribute('data-popup-sold-out-text') ||
      quickViewSettings.soldOutText ||
      'Sold Out';

    this.applyModeUI();

    const title = triggerButton.getAttribute('data-product-title');
    this.defaultImageSrc = triggerButton.getAttribute('data-product-image') || '';
    this.currentVariants = parseVariantsJson(triggerButton.getAttribute('data-variants'));

    if (!this.currentVariants.length) return;

    this.qvTitle.textContent = title;
    this.setVariantImage(this.defaultImageSrc, false);

    this.renderSwatches();

    const firstAvailableIndex = this.currentVariants.findIndex((variant) => this.isVariantAvailable(variant));
    if (firstAvailableIndex >= 0) {
      this.selectVariant(firstAvailableIndex, true);
    } else {
      this.showAllSoldOutState();
    }

    document.body.classList.add('quick-view-active');
    this.showOverlay();
    this.classList.remove('hidden');
    this.classList.remove('qv-is-closing');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('qv-is-open');
        this.overlayElement?.classList.add('qv-overlay-visible');
      });
    });
  }

  isVariantAvailable(variant) {
    return Boolean(variant?.available);
  }

  renderSwatches() {
    if (!this.swatchContainer) return;
    this.swatchContainer.innerHTML = '';

    this.currentVariants.forEach((variant, index) => {
      const isAvailable = this.isVariantAvailable(variant);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `qv-swatch${isAvailable ? '' : ' is-sold-out'}`;
      button.dataset.index = index;

      button.textContent = variant.title;

      if (!isAvailable) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-label', `${variant.title} - Sold out`);
      }

      this.swatchContainer.appendChild(button);
    });
  }

  updateVariantPricing(variant) {
    if (!variant) return;

    const price = Number(variant.price) || 0;
    const compareAt = Number(variant.compare_at_price) || 0;
    const hasDiscount = compareAt > price;

    if (this.qvPrice) {
      this.qvPrice.textContent =
        variant.price_formatted || (price > 0 ? formatVariantPrice(price) : '');
    }

    if (this.qvMrp) {
      if (hasDiscount) {
        this.qvMrp.textContent =
          variant.compare_at_price_formatted || formatVariantPrice(compareAt);
        this.qvMrp.classList.remove('hidden');
      } else {
        this.qvMrp.textContent = '';
        this.qvMrp.classList.add('hidden');
      }
    }

    if (this.qvDiscount) {
      if (hasDiscount) {
        const discountPercent = Math.round(((compareAt - price) / compareAt) * 100);
        if (discountPercent > 0) {
          this.qvDiscount.textContent = `${discountPercent}% OFF`;
          this.qvDiscount.classList.remove('hidden');
        } else {
          this.qvDiscount.textContent = '';
          this.qvDiscount.classList.add('hidden');
        }
      } else {
        this.qvDiscount.textContent = '';
        this.qvDiscount.classList.add('hidden');
      }
    }
  }

  getVariantImageSrc(variant) {
    if (!variant) return '';

    if (variant.image_url) return variant.image_url;

    const featuredImage = variant.featured_image;
    if (typeof featuredImage === 'string') return featuredImage;
    if (featuredImage?.src) return featuredImage.src;
    if (featuredImage?.url) return featuredImage.url;

    const featuredMedia = variant.featured_media;
    if (featuredMedia?.preview_image?.src) return featuredMedia.preview_image.src;
    if (featuredMedia?.src) return featuredMedia.src;

    const image = variant.image;
    if (typeof image === 'string') return image;
    if (image?.src) return image.src;

    return '';
  }

  setVariantImage(src, preload = true) {
    if (!this.qvImage || !src) return;

    const currentSrc = this.qvImage.getAttribute('src');
    if (currentSrc === src) return;

    if (!preload) {
      this.qvImage.setAttribute('src', src);
      return;
    }

    const nextImage = new Image();
    nextImage.onload = () => {
      if (this.qvImage) this.qvImage.setAttribute('src', src);
    };
    nextImage.src = src;
  }

  selectVariant(index, isInitial = false) {
    const variant = this.currentVariants[index];
    if (!variant) return;

    if (!this.isVariantAvailable(variant)) return;

    this.selectedVariant = variant;

    this.querySelectorAll('.qv-swatch').forEach((swatch, i) => {
      const isActive = i === index;
      if (swatch.classList.contains('active') !== isActive) {
        swatch.classList.toggle('active', isActive);
      }
    });

    if (this.qvSelectedValue) {
      this.qvSelectedValue.textContent = variant.title;
    }

    this.updateVariantPricing(variant);

    const nextImageSrc =
      this.getVariantImageSrc(variant) ||
      this.defaultImageSrc ||
      this.qvImage?.getAttribute('src') ||
      '';
    this.setVariantImage(nextImageSrc, !isInitial);

    const imgSrc = nextImageSrc;

    // Synchronize selected data attributes across active submission buttons
    [this.addToBoxBtn, this.addToBoxCrossBtn, this.addToCartBtn].forEach(btn => {
      btn?.setAttribute('data-variant-id', variant.id);
      btn?.setAttribute('data-product-image', imgSrc);
      btn?.setAttribute('data-variant-price', variant.price);
      btn?.setAttribute('data-variant-compare-at-price', variant.compare_at_price || 0);
    });

    this.updateActionButtons(true);
    this.updateModalButtonUI(variant.id);
  }

  updateActionButtons(isAvailable) {
    const activeBtn = this.isBundleMode ? this.addToBoxBtn : this.addToCartBtn;
    if (!activeBtn) return;

    activeBtn.disabled = !isAvailable;

    if (this.isBundleMode) {
      const textEl = activeBtn.querySelector('.bundle-btn-text');
      if (textEl) {
        textEl.textContent = isAvailable ? this.popupAddButtonText : this.popupSoldOutText;
      }
      return;
    }

    activeBtn.textContent = isAvailable ? 'Add to Cart' : 'Sold Out';
  }

  showAllSoldOutState() {
    const variant = this.currentVariants[0];
    if (!variant) return;

    this.selectedVariant = variant;
    this.qvSelectedValue.textContent = 'Sold out';

    this.updateVariantPricing(variant);
    this.updateActionButtons(false);
  }

  updateModalButtonUI(variantId) {
    if (!this.isBundleMode || !this.addToBoxWrapper) return;

    const stringId = String(variantId);
    const spans = document.querySelectorAll('.bundle_image-container span[class^="bundle_image-box_"]');
    const isAlreadyAdded = Array.from(spans).some(span => span.getAttribute('data-added-product') === stringId);

    if (isAlreadyAdded) {
      this.addToBoxWrapper.classList.add('is-added');
    } else {
      this.addToBoxWrapper.classList.remove('is-added');
    }
  }

  initAddToBoxHandlers() {
    this.addToBoxBtn?.addEventListener('click', () => {
      if (!this.isBundleMode) return;
      if (this.addToBoxBtn.disabled || !this.isVariantAvailable(this.selectedVariant)) return;

      const variantId = this.addToBoxBtn.getAttribute('data-variant-id');
      const imgUrl = this.addToBoxBtn.getAttribute('data-product-image');
      const price = this.addToBoxBtn.getAttribute('data-variant-price');
      const compareAtPrice = this.addToBoxBtn.getAttribute('data-variant-compare-at-price');

      const spans = document.querySelectorAll('.bundle_image-container span[class^="bundle_image-box_"]');
      const isFull = Array.from(spans).every(span => span.getAttribute('data-added-product'));

      if (isFull) {
        alert('Your box is full!');
        return;
      }

      if (typeof window.addProductToBox === 'function') {
        window.addProductToBox(variantId, imgUrl, price, compareAtPrice);
      }

      if (typeof window.syncProductCardStates === 'function') {
        window.syncProductCardStates();
      }

      this.syncAndCloseBundleFlow(variantId);
    });

    this.addToBoxCrossBtn?.addEventListener('click', () => {
      if (!this.isBundleMode) return;

      const variantId = this.addToBoxCrossBtn.getAttribute('data-variant-id');

      if (typeof window.toggleClose === 'function') {
        window.toggleClose(variantId);
      }

      this.syncAndCloseBundleFlow(null);
    });
  }

  /**
   * Global listener capturing cross-button drops triggered from the main product layout card grid.
   */
  initParentGridCrossHandler() {
    document.addEventListener('click', (e) => {
      if (!document.querySelector('.sticky-added-product-container')) return;

      const crossBtn = e.target.closest('.qv-parent-cross');
      if (!crossBtn) return;

      const activeGridVariantId = crossBtn.getAttribute('data-active-variant-id');
      if (!activeGridVariantId) return;

      e.preventDefault();
      e.stopPropagation();

      if (typeof window.toggleClose === 'function') {
        window.toggleClose(activeGridVariantId);
      }
    });
  }

  /**
   * Refactored synchronization method using saved parent references to toggle card states accurately.
   */
  syncAndCloseBundleFlow(lastAddedVariantId) {
    const spans = document.querySelectorAll('.bundle_image-container span[class^="bundle_image-box_"]');
    const activeBoxIds = Array.from(spans).map(span => span.getAttribute('data-added-product')).filter(Boolean);

    if (this.activeCardWrapper) {
      const singleVariantBtn = this.activeCardWrapper.querySelector('.bundle-buy-btn[data-product-id]');
      const gridCrossBtn = this.activeCardWrapper.querySelector('.qv-parent-cross');

      if (singleVariantBtn) {
        const id = singleVariantBtn.getAttribute('data-product-id');
        this.activeCardWrapper.classList.toggle('is-added', activeBoxIds.includes(id));
      } else {
        // Multi-variant validation route
        const activeVariantInBox = this.currentVariants.find(v => activeBoxIds.includes(String(v.id)));

        if (activeVariantInBox) {
          this.activeCardWrapper.classList.add('is-added');
          if (gridCrossBtn) {
            const passedId = lastAddedVariantId ? String(lastAddedVariantId) : String(activeVariantInBox.id);
            gridCrossBtn.setAttribute('data-active-variant-id', passedId);
          }
        } else {
          this.activeCardWrapper.classList.remove('is-added');
          gridCrossBtn?.removeAttribute('data-active-variant-id');
        }
      }
    }

    if (typeof window.updateBundleState === 'function') {
      window.updateBundleState();
    }

    this.hide();
  }

  initAddToCartHandler() {
    this.addToCartBtn?.addEventListener('click', async () => {
      if (this.isBundleMode) return;
      if (this.addToCartBtn.disabled || !this.isVariantAvailable(this.selectedVariant)) return;

      const variantId = this.addToCartBtn.getAttribute('data-variant-id');
      if (!variantId) return;

      const btn = this.addToCartBtn;
      btn.disabled = true;
      const originalText = btn.textContent.trim();
      btn.textContent = 'Adding…';

      try {
        const response = await fetch(window.Shopify?.routes?.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
        });

        if (!response.ok) throw new Error('Cart add failed');

        btn.textContent = 'Added!';
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

        setTimeout(() => {
          btn.textContent = originalText;
          this.hide();
        }, 1200);

      } catch (err) {
        console.error('Error adding to cart:', err);
        alert('Something went wrong. Please try again.');
        btn.textContent = originalText;
      } finally {
        btn.disabled = false;
      }
    });
  }

  showOverlay() {
    if (!this.overlayElement) {
      this.overlayElement = document.createElement('div');
      this.overlayElement.className = 'qv-modal-overlay';
      this.overlayElement.addEventListener('click', () => this.hide());
      document.body.appendChild(this.overlayElement);
    }
  }

  removeOverlay() {
    if (this.overlayElement) {
      this.overlayElement.classList.remove('qv-overlay-visible');
      this.overlayElement.remove();
      this.overlayElement = null;
    }
  }

  hide() {
    if (this.classList.contains('hidden') || this.isClosing) return;

    this.isClosing = true;
    this.classList.remove('qv-is-open');
    this.overlayElement?.classList.remove('qv-overlay-visible');

    if (this.closeTimer) clearTimeout(this.closeTimer);

    this.closeTimer = setTimeout(() => {
      document.body.classList.remove('quick-view-active');
      this.removeOverlay();
      this.classList.add('hidden');
      this.classList.remove('qv-is-closing');
      this.isClosing = false;
      this.closeTimer = null;
    }, getQuickViewAnimationMs());
  }
}

if (!customElements.get('custom-quick-view')) {
  customElements.define('custom-quick-view', CustomQuickView);
}